"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion } from "framer-motion";
import { NavArrowDown, NavArrowUp, Search, Xmark } from "iconoir-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Helper function to highlight search terms
const highlightText = (text: string, searchQuery: string) => {
  if (!searchQuery.trim()) return text;

  const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  );
};

export default function FAQ() {
  const { openModal, closeModal, isModalOpen } = useModalManager();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const faqs = [
    {
      question: "What types of events do you handle?",
      answer:
        "We specialize in group events with 10+ sleeping rooms. This includes: company all-hands, executive and leadership retreats, sales kickoffs and incentive trips, President's Club retreats, product or engineering offsites, and conferences and summits. If it involves a team and travel, we've got you covered.",
    },
    {
      question: "Where do you plan events?",
      answer:
        "Anywhere in the world. 🌍 We've planned events across North America, Europe, Asia, and beyond—from a 12-person exec retreat in Cabo to a 500-person company offsite in Thailand. Our global venue database spans 400,000+ venues, and we collaborate with trusted local experts to ensure you get the most up-to-date recommendations and insider access.",
    },
    {
      question: "What's your scope of work?",
      answer:
        "We handle everything end-to-end: destination research based on your team's locations, budgeting and flight cost estimation, hotel sourcing, outreach, and negotiation, venue and vendor contracting, private dinners, activities, and team-building, registration and RSVP tools. Once contracts are finalized, you'll be introduced directly to the vendors for execution. We streamline the hard stuff - so you can focus on bringing your team together.",
    },
    {
      question: "What companies use Nowadays?",
      answer:
        "We work with companies of all sizes - from fast-growing startups to global enterprises. Some of our clients include: Series A–C startups like Pylon and Supabase, and enterprise teams at Google, BlackRock, and Northstar Meetings Group. We also white-label our platform for event agencies, giving them access to our tech-powered sourcing and planning tools to better serve their own clients. Whether you're a 20-person team or a 2,000-person org, we tailor our approach to fit your goals.",
    },
    {
      question: "Are work retreats worth it?",
      answer:
        "Absolutely. Company retreats do more than just break routine - they boost employee engagement, strengthen team bonds, and create space for strategic thinking away from daily distractions. In fact, 60% of business travelers and corporate travel decision-makers believe remote work will increase the importance of team gatherings. Many employees are eager to reconnect in person, and retreats play a powerful role in retention, morale, and company culture.",
    },
    {
      question: "What qualifies as a corporate retreat?",
      answer:
        "A corporate retreat is any out-of-office gathering designed to bring your team together - whether it's to plan, bond, or simply recharge. Some are structured with meetings and workshops; others are focused on relaxation, nature, or shared adventure. The format is up to you - but the goal is the same: create meaningful time together.",
    },
    {
      question: "How long should a corporate retreat last?",
      answer:
        "Most retreats last anywhere from 2 to 5 days, depending on your goals and travel distance. If you're staying local, a 2- to 3-day retreat often does the trick. For international or long-haul destinations, we recommend at least 4–5 days to make the most of the time and travel.",
    },
    {
      question: "What's another name for a corporate retreat?",
      answer:
        "You might hear them called: Company Offsite, Team Retreat, Team-Building Escape, Workation, Strategy Session, or Corporate Adventure. No matter the name, it's about creating a shared experience that's both memorable and productive.",
    },
    {
      question: "What's the deposit?",
      answer:
        "We take a $250 deposit via Stripe to kick off planning. It's fully refundable if you don't move forward, and it gets applied as credit toward your event. The main reason we do this is to ensure high intent—it helps us avoid wasting hotels' time with non-committed inquiries and lets us prioritize serious clients.",
    },
    {
      question: "What's the 7% sourcing fee?",
      answer:
        "This covers all the outreach, rate negotiation, and proposal comparisons we handle on your behalf—similar to what third-party planners charge, but at a fraction of the price. We're also an IATA-certified agency, which means hotels sometimes pay us a small commission (from their marketing budget). But we only accept commission if it doesn't impact your rate, and we include a clause in every contract to guarantee that. Most of the time, our rates are 20–30% lower than what you'd get by going direct—and we'll always tell you if that's not the case. Still have questions? We're always happy to chat.",
    },
    {
      question: "What is commission?",
      answer:
        "Great question! We're an IATA-certified hotel sourcing agency, which means we earn a % commission directly from hotels - paid out of their marketing budget, not yours. It doesn't affect your rate at all, and we include a clause in every contract to ensure the hotel can't raise your rate because of it. In fact, because we bring hotels consistent group business, we're usually able to negotiate rates 20–30% lower than what you'd get going direct. We also secure added perks like waived fees, better concessions, and more flexible terms as part of the process.",
    },
    {
      question: "How are you different from Booking.com?",
      answer:
        "Booking.com is designed for individual travel, not group bookings. In fact, if you're booking more than 10 sleeping rooms, most hotels won't even allow it through Booking.com - they'll refer you to their group sales team instead. You'll need to call or submit a RFP (Request for Proposal) online and wait for a week to hear back. That's where we come in. We already established relationships and work directly with hotel group sales teams to unlock discounted rates, waived fees, and added perks that aren't available online. For example, one of our clients recently booked the Beacon Grand for $140/night, while the same dates were listed at $238/night on Booking.com. Because we bring hotels consistent group business, they offer us their best pricing and flexibility—and we pass those savings on to you.",
    },
    {
      question: "Why Nowadays for your company retreat?",
      answer:
        "At Nowadays, we make planning unforgettable company retreats effortless. From finding the perfect venue to curating activities and handling logistics, our expert team handles it all - so you don't have to. We've helped hundreds of teams (from fast-growing startups to Fortune 500s) design retreats that build culture, spark creativity, and bring people together. Whether you're dreaming of a beachside escape or a mountain adventure, we'll tailor every detail to your goals and vibe. We believe retreats should be stress-free to plan and powerful for your team. That's why we combine personalized support with tools that simplify every step—so you can focus on what really matters: connecting your people. Ready to plan something your team will actually remember? Let's chat.",
    },
  ];

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  // Filter FAQs based on search query
  const filteredFaqs = useMemo(() => {
    if (!searchQuery.trim()) return faqs;

    const query = searchQuery.toLowerCase();
    return faqs.filter((faq) => faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query));
  }, [searchQuery]);

  const toggleQuestion = (index: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  // Debounced search
  const debouncedSetSearchQuery = useCallback((value: string) => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(value);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const cleanup = debouncedSetSearchQuery(searchInput);
    return cleanup;
  }, [searchInput, debouncedSetSearchQuery]);

  const toggleAllExpanded = () => {
    const allExpanded = filteredFaqs.length > 0 && filteredFaqs.every((_, index) => expandedQuestions.has(index));
    if (allExpanded) {
      setExpandedQuestions(new Set());
    } else {
      setExpandedQuestions(new Set(filteredFaqs.map((_, index) => index)));
    }
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
  };

  const allExpanded = filteredFaqs.length > 0 && filteredFaqs.every((_, index) => expandedQuestions.has(index));

  // Keyboard shortcut for search (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <main className="container-page">
      {/* FAQ Section */}
      <section className="container-top-form">
        <div>
          <div className="text-center flex flex-col gap-4 mb-12">
            <h2 className="text-h2 text-landing-primary-sand">Common Questions</h2>
            <p className="text-body-lg text-landing-secondary-sand max-w-[530px] mx-auto">
              Can't find what you're looking for? Contact us and we'll get back to you as soon as possible.
            </p>
          </div>

          {/* Search and Controls */}
          <div className="max-w-4xl mx-auto mb-6">
            {/* Search Bar */}
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search questions... (⌘K)"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-200 text-body-sm bg-white"
              />
              {searchInput && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Xmark className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div key={`${searchQuery}-${filteredFaqs.length}`} className="text-sm text-landing-secondary-sand">
                {searchQuery ? (
                  <div className="flex items-center gap-2">
                    <span>
                      <span className="font-medium text-landing-primary-sand">{filteredFaqs.length}</span> of{" "}
                      {faqs.length} questions
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      "{searchQuery}"
                    </span>
                  </div>
                ) : (
                  <span>
                    <span className="font-medium text-landing-primary-sand">{faqs.length}</span> questions
                  </span>
                )}
              </div>

              {filteredFaqs.length > 0 && (
                <button
                  onClick={toggleAllExpanded}
                  className="text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-1 rounded hover:bg-gray-100"
                >
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              )}
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <Search className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-body-lg font-medium text-landing-primary-sand mb-2">No questions found</h3>
                <p className="text-body-sm text-landing-secondary-sand mb-3">
                  Try adjusting your search terms or{" "}
                  <button onClick={clearSearch} className="text-blue-600 hover:text-blue-800 underline">
                    clear your search
                  </button>
                  .
                </p>
                <Button variant="outline" size="sm" onClick={() => openModal("demo")} className="text-xs">
                  Contact Us
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredFaqs.map((faq, index) => (
                  <div
                    key={`${searchQuery}-${index}`}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-gray-300 transition-all duration-200"
                  >
                    <button
                      onClick={() => toggleQuestion(index)}
                      className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex-1 pr-3">
                        <p className="text-tag-md font-medium text-landing-primary-sand group-hover:text-blue-600 transition-colors">
                          {highlightText(faq.question, searchQuery)}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-6 h-6 rounded-full bg-gray-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                          {expandedQuestions.has(index) ? (
                            <NavArrowUp className="w-3 h-3 text-gray-600 group-hover:text-blue-600 transition-colors" />
                          ) : (
                            <NavArrowDown className="w-3 h-3 text-gray-600 group-hover:text-blue-600 transition-colors" />
                          )}
                        </div>
                      </div>
                    </button>
                    {expandedQuestions.has(index) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        className="border-t border-gray-100"
                      >
                        <div className="px-4 pt-3 pb-4">
                          <p className="text-body text-landing-secondary-sand leading-relaxed">
                            {highlightText(faq.answer, searchQuery)}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Schedule a Call"
        subtitle="Get in touch and we'll respond within 30 minutes"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Schedule your call with our team"
      />
    </main>
  );
}
